import { useEffect } from 'react';

const SITE_NAME = 'Radiology Illustration';
const SITE_URL = 'https://radiologyillustration.com';

type SeoProps = {
    title: string;
    description: string;
    canonicalPath?: string;
    type?: 'website' | 'article';
    image?: string | null;
    noIndex?: boolean;
    jsonLd?: Record<string, unknown>;
};

const setMetaTag = (selector: string, attributeName: 'name' | 'property', attributeValue: string, content: string) => {
    let tag = document.head.querySelector<HTMLMetaElement>(selector);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attributeName, attributeValue);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
};

const removeMetaTag = (selector: string) => {
    document.head.querySelector<HTMLMetaElement>(selector)?.remove();
};

const setCanonical = (href: string) => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', href);
};

const setJsonLd = (jsonLd?: Record<string, unknown>) => {
    const scriptId = 'route-json-ld';
    const existing = document.getElementById(scriptId);

    if (!jsonLd) {
        existing?.remove();
        return;
    }

    const script = existing || document.createElement('script');
    script.id = scriptId;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(jsonLd);

    if (!existing) {
        document.head.appendChild(script);
    }
};

const Seo = ({
    title,
    description,
    canonicalPath = '/',
    type = 'website',
    image,
    noIndex = false,
    jsonLd,
}: SeoProps) => {
    useEffect(() => {
        const fullTitle = title === SITE_NAME ? SITE_NAME : `${title} | ${SITE_NAME}`;
        const canonicalUrl = `${SITE_URL}${canonicalPath}`;
        const absoluteImage = image?.startsWith('http') ? image : image ? `${SITE_URL}${image}` : '';

        document.title = fullTitle;

        setMetaTag('meta[name="description"]', 'name', 'description', description);
        setMetaTag('meta[name="robots"]', 'name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
        setMetaTag('meta[property="og:type"]', 'property', 'og:type', type);
        setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
        setMetaTag('meta[property="og:title"]', 'property', 'og:title', fullTitle);
        setMetaTag('meta[property="og:description"]', 'property', 'og:description', description);
        setMetaTag('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
        setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', absoluteImage ? 'summary_large_image' : 'summary');
        setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);
        setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', description);

        if (absoluteImage) {
            setMetaTag('meta[property="og:image"]', 'property', 'og:image', absoluteImage);
            setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', absoluteImage);
        } else {
            removeMetaTag('meta[property="og:image"]');
            removeMetaTag('meta[name="twitter:image"]');
        }

        setCanonical(canonicalUrl);
        setJsonLd(jsonLd);
    }, [title, description, canonicalPath, type, image, noIndex, jsonLd]);

    return null;
};

export default Seo;
